// ============================================================================
// REGION HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { regionService } from '../../api/services/regionService';
import type {
  Region,
  RegionListItem,
  CreateRegionRequest,
  UpdateRegionRequest,
  RegionSearchRequest,
} from '../../api/types/region.types';
import type { ID } from '../../api/types';

/** List hook — paginated, re-fetches whenever params change. */
export const useRegions = (params?: RegionSearchRequest) => {
  const [data, setData] = useState<RegionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchRegions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await regionService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  return { data, loading, error, pagination, refetch: fetchRegions };
};

/** Single-region hook by ID. */
export const useRegion = (id: ID | undefined) => {
  const [data, setData] = useState<Region | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchRegion = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await regionService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRegion();
  }, [fetchRegion]);

  return { data, loading, error, refetch: fetchRegion };
};

/** Create mutation. */
export const useCreateRegion = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createRegion = async (
    data: CreateRegionRequest,
    options?: { onSuccess?: (region: Region) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await regionService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { createRegion, loading, error };
};

/** Update mutation. */
export const useUpdateRegion = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateRegion = async (
    id: ID,
    data: UpdateRegionRequest,
    options?: { onSuccess?: (region: Region) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await regionService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { updateRegion, loading, error };
};

/** Delete mutation (soft delete). */
export const useDeleteRegion = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteRegion = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    try {
      await regionService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { deleteRegion, loading, error };
};
